import { z } from 'zod'

// ============================================================================
// Regex patterns
// ============================================================================

/** Thai characters + spaces + hyphens + dots, 2-100 chars */
export const THAI_NAME_REGEX = /^[\u0E00-\u0E7F\s\-\.]{2,100}$/

/** Thai phone: 0 followed by 8 or 9 digits */
export const THAI_PHONE_REGEX = /^0[0-9]{8,9}$/

/** Nickname: Thai, English, digits, spaces, 1-30 chars */
export const NICKNAME_REGEX = /^[\u0E00-\u0E7Fa-zA-Z0-9\s]{1,30}$/

// ============================================================================
// Shared Zod schemas
// ============================================================================

export const thaiNameSchema = z
  .string()
  .min(2, 'ชื่อต้องมีอย่างน้อย 2 ตัวอักษร')
  .max(100, 'ชื่อต้องไม่เกิน 100 ตัวอักษร')
  .regex(THAI_NAME_REGEX, 'กรุณากรอกชื่อภาษาไทย')

export const thaiPhoneSchema = z
  .string()
  .regex(THAI_PHONE_REGEX, 'กรุณากรอกเบอร์โทรที่ถูกต้อง เช่น 0812345678')

export const nicknameSchema = z
  .string()
  .min(1, 'กรุณากรอกชื่อเล่น')
  .max(30, 'ชื่อเล่นต้องไม่เกิน 30 ตัวอักษร')
  .regex(NICKNAME_REGEX, 'ชื่อเล่นต้องเป็นภาษาไทย อังกฤษ หรือตัวเลข')

export const studentNumberSchema = z
  .number()
  .int('เลขที่ต้องเป็นจำนวนเต็ม')
  .min(1, 'เลขที่ต้องอยู่ระหว่าง 1-60')
  .max(60, 'เลขที่ต้องอยู่ระหว่าง 1-60')

export const noteSchema = z
  .string()
  .max(200, 'หมายเหตุต้องไม่เกิน 200 ตัวอักษร')
  .transform((val) => val.replace(/<[^>]*>/g, ''))

export const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'รูปแบบวันที่ไม่ถูกต้อง')

export const uuidSchema = z.string().uuid('รหัสไม่ถูกต้อง')

// ============================================================================
// Entity schemas
// ============================================================================

export const createStudentSchema = z.object({
  studentNumber: studentNumberSchema,
  prefix: z.enum(['ด.ช.', 'ด.ญ.'], { message: 'กรุณาเลือกคำนำหน้า' }),
  fullName: thaiNameSchema,
  nickname: nicknameSchema.optional(),
  note: noteSchema.optional(),
})

export const updateStudentSchema = z.object({
  studentNumber: studentNumberSchema.optional(),
  prefix: z.enum(['ด.ช.', 'ด.ญ.'], { message: 'กรุณาเลือกคำนำหน้า' }).optional(),
  fullName: thaiNameSchema.optional(),
  nickname: nicknameSchema.optional().nullable(),
  note: noteSchema.optional().nullable(),
})

export const createHomeworkSchema = z.object({
  subject: z.string().min(1, 'กรุณาเลือกวิชา'),
  title: z.string().min(1, 'กรุณากรอกชื่อการบ้าน').max(200, 'ชื่อการบ้านต้องไม่เกิน 200 ตัวอักษร'),
  description: z.string().max(1000, 'รายละเอียดต้องไม่เกิน 1000 ตัวอักษร').optional(),
  dueDate: dateSchema,
})

export const createAttendanceSchema = z.object({
  date: dateSchema,
  records: z.array(
    z.object({
      studentId: uuidSchema,
      status: z.enum(['present', 'absent', 'leave'], {
        message: 'สถานะไม่ถูกต้อง',
      }),
      note: noteSchema.optional(),
    }),
  ),
})

export const guardianRequestSchema = z.object({
  studentNameText: z.string().min(1, 'กรุณากรอกชื่อนักเรียน'),
  guardianName: thaiNameSchema,
  relationship: z.string().min(1, 'กรุณาเลือกความสัมพันธ์'),
  phone: thaiPhoneSchema,
})
